import { useCallback, useMemo } from 'react';
import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import ListHeader, { ListHeaderType } from '@/components/ListHeader';
import StatusTag from '@/components/StatusTag';
import { getEndpointWithParams } from '@/routes/config';
import { Candidate } from '@/services/candidates';
import Pagination, { PaginationTypes } from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { CandidateTechStacksType } from '@/types';
import { Paths } from '@/types/routes';
import { getMainTechSkillsFormatted } from '@/utils/candidate';
import * as Styles from './TalentPoolList.styles';
import { getSortByOptions } from './TalentPoolList.utils';

type TalentPoolListComponentProps = PaginationTypes &
  ListHeaderType & {
    candidatesList: Candidate[];
    isLoading: boolean;
  };

const CandidatesListComponent = function ({
  candidatesList,
  isLoading,
  onUpdateSortBy,
  onChangePageHandler,
  onChangeRecordsPerPage,
  count,
  itemsPerPage,
  lastPage,
  currentPage,
  ...props
}: TalentPoolListComponentProps): JSX.Element {
  const renderHeader = useMemo(() => {
    return (
      <Tr>
        <Th>Candidate Name</Th>
        <Th>Main Tech Stack</Th>
        <Th>Experience</Th>
        <Th>Status</Th>
        <Th>Availability</Th>
        <Th>Email</Th>
      </Tr>
    );
  }, []);

  const renderRow = useCallback((data: Candidate) => {
    const techStackFormatted = getMainTechSkillsFormatted(
      data?.skills as CandidateTechStacksType[]
    );

    const candidateLink = getEndpointWithParams(Paths.TALENT_POOL_DETAILS, {
      id: data.id,
    });

    return (
      <Tr key={data.id}>
        <Td>
          <Styles.Centered>
            <Styles.CustomUserInitials
              pictureUrl={data?.pictureUrl ?? ''}
              firstName={data.firstName}
              lastName={data?.lastName ?? ''}
            />
            <Styles.TableLink to={candidateLink} className="capitalize">
              {data.firstName} {data.lastName}
            </Styles.TableLink>
          </Styles.Centered>
        </Td>

        <Td>
          {techStackFormatted.map((stack, index) => (
            <Styles.CustomTechStackBadge
              key={`${stack}-${data.id}-${index.toString()}`}
              title={stack}
            />
          ))}
        </Td>

        <Td>{data?.experience}</Td>

        <Td>{data.status && <StatusTag status={data.status} />}</Td>

        <Td>{data?.availability}</Td>
        <Td>{data?.email}</Td>
      </Tr>
    );
  }, []);

  const renderEmptyStateRows = (i: number): JSX.Element => (
    <Tr key={i}>
      <Td>
        <div className="tw-flex tw-items-center">
          <SkeletonCircle width={6} height={6} className="tw-mr-3" />
          <Skeleton className="tw-h-3.5 tw-w-[100px]" />
        </div>
      </Td>
      <Td>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <Skeleton className="tw-h-[21px] tw-w-[56px]" />
          <Skeleton className="tw-h-[21px] tw-w-[40px]" />
        </div>
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[60px]" />
      </Td>
      <Td>
        <div className="tw-flex tw-items-center">
          <SkeletonCircle className="!tw-w-[5px] !tw-h-[5px] tw-mr-[5px]" />
          <Skeleton className="tw-h-3.5 tw-w-[60px]" />
        </div>
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
    </Tr>
  );

  return (
    <Styles.Content data-testid="talent-pool-list">
      <ListHeader
        {...props}
        showSearchBar={false}
        onUpdateSortBy={onUpdateSortBy}
        title="Talent Pool"
        sortByOptions={getSortByOptions()}
      />
      {!candidatesList.length && !isLoading ? (
        <Styles.NotFound>
          <Styles.NotFoundSubtitle>Your search did not match any candidate</Styles.NotFoundSubtitle>
          <p>Try to set different filters.</p>
          <p>You can always use the search bar to find candidates too.</p>
        </Styles.NotFound>
      ) : (
        <>
          {isLoading ? (
            <Table
              renderHeader={renderHeader}
              renderRow={renderEmptyStateRows}
              data={[...Array(itemsPerPage).keys()].map((key) => key + 1)}
            />
          ) : (
            <Table renderHeader={renderHeader} renderRow={renderRow} data={candidatesList} />
          )}
        </>
      )}

      <Pagination
        onChangeRecordsPerPage={onChangeRecordsPerPage}
        lastPage={lastPage}
        itemsPerPage={itemsPerPage}
        count={count}
        currentPage={currentPage}
        onChangePageHandler={onChangePageHandler}
      />
    </Styles.Content>
  );
};

export default CandidatesListComponent;
